/**
 * Case history for a ticket: notes and call recordings, newest first.
 *
 * Two sources are merged here. Ticket interactions come from the CRM API,
 * and the linked order's role remarks come from the staff order payload —
 * the same rows the order page shows under "Remarks & call recordings".
 * Before, a call recorded from the order side (or by the staff app) was
 * invisible on the ticket, so agents had to leave the ticket to hear it.
 * Order-sourced rows are read-only and labelled with where they came from.
 *
 * Shares the audio playback approach with `OrderRemarksPanel`: relative
 * MEDIA paths are resolved through the API base URL, falling back to the
 * dev-server proxy in dev.
 */
import { useMemo, type ReactNode } from 'react';
import {
  Headphones,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  Receipt,
  ShieldCheck,
  StickyNote,
  User as UserIcon,
} from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';
import type { CrmAudience, CrmInteraction } from '@/api/bi/crm';
import type { OrderRemark } from '@/api/legacy/orders';

const AUDIENCE_LABEL: Record<CrmAudience, string> = {
  packer: 'Packer',
  biller: 'Biller',
  rider: 'Rider',
  customer: 'Customer',
  store_manager: 'Store Manager',
  internal: 'Internal',
};

/** Order-side remark buckets, keyed by the role the remark belongs to. */
export interface OrderRemarkBuckets {
  packer?: OrderRemark[] | null;
  biller?: OrderRemark[] | null;
  rider?: OrderRemark[] | null;
  customer?: OrderRemark[] | null;
}

function resolveMediaUrl(file: string | null | undefined): string | null {
  if (!file) return null;
  if (/^https?:\/\//i.test(file)) return file;
  const base = import.meta.env.DEV
    ? ''
    : (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');
  return `${base}${file.startsWith('/') ? '' : '/'}${file}`;
}

interface TimelineEntry {
  key: string;
  origin: 'ticket' | 'order';
  at: string | null;
  author: string;
  roleLabel: string;
  isCall: boolean;
  isOutgoing: boolean;
  isInternal: boolean;
  note: string | null;
  audioUrl: string | null;
  durationSeconds: number | null;
  contactName: string | null;
  contactPhone: string | null;
  via: string | null;
}

function fromInteraction(i: CrmInteraction): TimelineEntry {
  return {
    key: `ticket-${i.id}`,
    origin: 'ticket',
    at: i.called_at ?? i.created_at,
    author: i.actor_user?.name ?? i.contacted_name ?? 'Unknown',
    roleLabel: AUDIENCE_LABEL[i.audience],
    isCall: Boolean(i.call_recording_url),
    isOutgoing: Boolean(i.is_outgoing),
    isInternal: i.audience === 'internal',
    note: i.note?.trim() ? i.note : null,
    audioUrl: resolveMediaUrl(i.call_recording_url),
    durationSeconds: i.duration_seconds,
    contactName: i.contacted_name || null,
    contactPhone: i.contacted_phone || null,
    via: i.actor_user?.name ?? null,
  };
}

function fromRemark(remark: OrderRemark, role: string, idx: number): TimelineEntry {
  const author = remark.support?.name?.trim() || remark.name?.trim() || 'Unknown';
  return {
    key: `order-${role}-${remark.id ?? 'r'}-${idx}`,
    origin: 'order',
    at: remark.called_at ?? remark.at ?? null,
    author,
    roleLabel: role,
    isCall: Boolean(remark.file),
    isOutgoing: Boolean(remark.is_outgoing),
    isInternal: false,
    note: remark.remarks?.trim() ? remark.remarks : null,
    audioUrl: resolveMediaUrl(remark.file),
    durationSeconds: remark.duration ?? null,
    contactName: remark.name?.trim() || null,
    contactPhone: remark.phone?.trim() || null,
    via: remark.support?.name?.trim() || null,
  };
}

function hasContent(remark: OrderRemark): boolean {
  return Boolean(
    (remark.remarks && remark.remarks.trim().length > 0) ||
      remark.file ||
      remark.support ||
      remark.phone,
  );
}

export function InteractionTimeline({
  interactions,
  orderRemarks,
  orderId,
  composer,
}: {
  interactions: CrmInteraction[];
  orderRemarks?: OrderRemarkBuckets | null;
  orderId?: number | null;
  /** Docked at the bottom of the card, under the history it appends to. */
  composer?: ReactNode;
}) {
  const entries = useMemo<TimelineEntry[]>(() => {
    const merged = interactions.map(fromInteraction);

    const buckets: Array<[string, OrderRemark[] | null | undefined]> = [
      ['Packer', orderRemarks?.packer],
      ['Biller', orderRemarks?.biller],
      ['Rider', orderRemarks?.rider],
      ['Customer', orderRemarks?.customer],
    ];
    for (const [role, items] of buckets) {
      (items ?? []).filter(hasContent).forEach((remark, idx) => {
        merged.push(fromRemark(remark, role, idx));
      });
    }

    return merged.sort((a, b) => {
      const ta = a.at ? new Date(a.at).getTime() : 0;
      const tb = b.at ? new Date(b.at).getTime() : 0;
      return tb - ta;
    });
  }, [interactions, orderRemarks]);

  const totalCalls = entries.filter((e) => e.audioUrl).length;
  const fromOrder = entries.filter((e) => e.origin === 'order').length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-2">
          <Headphones className="h-4 w-4" /> Conversation timeline
        </CardTitle>
        <div className="inline-flex flex-wrap items-center gap-2">
          {totalCalls > 0 ? (
            <Badge tone="info" className="gap-1">
              <Phone className="h-3 w-3" /> {totalCalls} recording{totalCalls === 1 ? '' : 's'}
            </Badge>
          ) : null}
          {fromOrder > 0 ? (
            <Badge tone="neutral" className="gap-1">
              <Receipt className="h-3 w-3" /> {fromOrder} from order
              {orderId != null ? ` #${orderId}` : ''}
            </Badge>
          ) : null}
          <Badge tone="neutral">{entries.length} entries</Badge>
        </div>
      </CardHeader>
      <CardBody className="p-0">
        {entries.length === 0 ? (
          <div className="px-5 py-6 text-center text-xs text-muted-foreground">
            Nothing logged yet — add the first note or recording below.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {entries.map((entry) => (
              <li key={entry.key} className="px-5 py-3">
                <TimelineRow entry={entry} />
              </li>
            ))}
          </ul>
        )}
        {composer ? (
          <div className="border-t border-border bg-muted/20 px-5 py-4">{composer}</div>
        ) : null}
      </CardBody>
    </Card>
  );
}

function TimelineRow({ entry }: { entry: TimelineEntry }) {
  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2">
          {entry.isCall ? (
            <span
              className={cn(
                'mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
                entry.isOutgoing ? 'bg-success/15 text-success' : 'bg-info/15 text-info',
              )}
              title={entry.isOutgoing ? 'Outgoing call' : 'Incoming call'}
            >
              {entry.isOutgoing ? (
                <PhoneOutgoing className="h-3.5 w-3.5" />
              ) : (
                <PhoneIncoming className="h-3.5 w-3.5" />
              )}
            </span>
          ) : (
            <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
              {entry.isInternal ? (
                <ShieldCheck className="h-3.5 w-3.5" />
              ) : (
                <StickyNote className="h-3.5 w-3.5" />
              )}
            </span>
          )}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
              <span className="truncate">{entry.author}</span>
              <Badge tone="neutral">{entry.roleLabel}</Badge>
              {entry.origin === 'order' ? (
                <Badge tone="info" className="gap-1">
                  <Receipt className="h-3 w-3" /> Order remark
                </Badge>
              ) : null}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {entry.contactPhone ? (
                <span className="inline-flex items-center gap-1">
                  <UserIcon className="h-3 w-3" />
                  {entry.contactName ?? ''}{' '}
                  <a href={`tel:${entry.contactPhone}`} className="hover:underline">
                    {entry.contactPhone}
                  </a>
                </span>
              ) : entry.via ? (
                <>via {entry.via}</>
              ) : null}
            </div>
          </div>
        </div>
        <div className="text-right text-[11px] text-muted-foreground">
          {entry.at ? fmt.datetime(entry.at) : '—'}
          {entry.durationSeconds != null ? (
            <div>{fmt.duration(entry.durationSeconds)}</div>
          ) : null}
        </div>
      </div>

      {entry.note ? (
        <p className="whitespace-pre-wrap text-xs text-foreground/90">{entry.note}</p>
      ) : null}

      {entry.audioUrl ? (
        <div className="rounded-md border border-border/60 bg-background p-2">
          <audio
            controls
            preload="none"
            src={entry.audioUrl}
            className="h-9 w-full"
            aria-label={`Call recording from ${entry.author}`}
          >
            <a href={entry.audioUrl} download>
              Download recording
            </a>
          </audio>
          <div className="mt-1 flex items-center justify-end">
            <a
              href={entry.audioUrl}
              download
              className="text-[11px] text-primary hover:underline"
            >
              Download
            </a>
          </div>
        </div>
      ) : null}
    </div>
  );
}
